import { useCallback, useEffect, useMemo, useState } from 'react'
import { Checkbox, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material'
import { apiGet } from '../../generic/Api_Functions'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { allowsPartialQuantity, allowsDecimalQuantity, normalizeInstanceQuantity } from '../../Mos/Widgets/MachineMaterial/instanceGranularity'

// Sun binds page/size/allSelected as primitives, so a request without a RequestPagination is
// rejected (400) before the controller runs. A reference's available instances is a small set, so a
// single large page is requested (the endpoint's in-memory pagination is built for small sets too).
const ALL_INSTANCES_REQUEST = { page: 0, size: 1000, allSelected: false }

// Instance picker for the "Load into machine storage" config flow (CU2). Lists the GENERAL-stock
// instances (status AVAILABLE) of the reference being loaded so the configurator chooses which
// instances to move. Granularity from the reference flags (fractionable/unitary, per row):
//   - whole-only (case 1): checkboxes -> instanceSelections: [{ instanceId }]
//   - partial (cases 2/3/4): a quantity per instance (integer for case 2, decimal for 3/4) capped at
//     quantityAvailable -> instanceSelections: [{ instanceId, quantity }]
// It does NOT load — on accept it returns the instanceSelections to LoadMachineStorageDialog.
const LoadMachineStorageInstancesDialog = ({ open = false, referenceId = 0, referenceCode = '', selectedInstanceSelections = [], onClose = () => {}, onConfirm = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const [instances, setInstances] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const [quantityByInstanceId, setQuantityByInstanceId] = useState({})

  // Seed the selection from what the caller already had and (re)load the reference's instances each
  // time the dialog opens for a reference. Only keyed on open/referenceId so a parent re-render does
  // not re-seed and wipe in-progress toggles.
  useEffect(() => {
    if (!open || !referenceId) return
    const seed = Array.isArray(selectedInstanceSelections) ? selectedInstanceSelections : []
    setSelectedIds(seed.map((item) => item.instanceId))
    setQuantityByInstanceId(seed.reduce((acc, item) => {
      if (item.quantity !== undefined && item.quantity !== null) acc[item.instanceId] = item.quantity
      return acc
    }, {}))
    apiGet('rawMaterials/machineStorage/reference/' + referenceId + '/generalStock/instances', ALL_INSTANCES_REQUEST)
      .then((result) => setInstances(Array.isArray(result) ? result : (result?.content ?? [])))
  }, [open, referenceId]) // eslint-disable-line react-hooks/exhaustive-deps

  const flags = instances.length > 0 ? { fractionable: instances[0].fractionable, unitary: instances[0].unitary } : {}
  const partial = allowsPartialQuantity(flags)
  const allowDecimal = allowsDecimalQuantity(flags)

  const toggleInstance = useCallback((id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((current) => current !== id) : [...prev, id]))
  }, [])

  const setQuantity = useCallback((id, value) => {
    setQuantityByInstanceId((prev) => ({ ...prev, [id]: value }))
  }, [])

  const allIds = useMemo(() => instances.map((row) => row.id), [instances])
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.includes(id))
  const someSelected = allIds.some((id) => selectedIds.includes(id))

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const everySelected = allIds.length > 0 && allIds.every((id) => prev.includes(id))
      return everySelected ? [] : [...allIds]
    })
  }, [allIds])

  const availableById = useMemo(
    () => instances.reduce((acc, row) => { acc[row.id] = row.quantityAvailable; return acc }, {}),
    [instances]
  )

  const selectedCount = partial
    ? Object.values(quantityByInstanceId).filter((value) => Number(value) > 0).length
    : selectedIds.length

  const confirmSelection = () => {
    let instanceSelections
    if (partial) {
      instanceSelections = Object.entries(quantityByInstanceId)
        .map(([instanceId, value]) => ({
          instanceId: Number(instanceId),
          quantity: normalizeInstanceQuantity(value, { decimal: allowDecimal, available: availableById[instanceId] })
        }))
        .filter((item) => item.quantity > 0)
    } else {
      instanceSelections = selectedIds.map((instanceId) => ({ instanceId }))
    }
    onConfirm(instanceSelections)
    onClose()
  }

  return (
    <SmartDialog
      title={createDialogTitle({ color: 'primary', title: t_('Select instances of {{reference}}', { reference: referenceCode }) })}
      setOpen={open}
      cancelCallback={onClose}
      acceptCallback={confirmSelection}
      renderComponent={
        <Stack spacing={1} sx={{ minWidth: '24rem' }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                {!partial && (
                  <TableCell padding='checkbox'>
                    <Checkbox
                      size='small'
                      checked={allSelected}
                      indeterminate={!allSelected && someSelected}
                      disabled={allIds.length === 0}
                      onChange={toggleSelectAll}
                      inputProps={{ 'aria-label': t_('Select all') }}
                    />
                  </TableCell>
                )}
                <TableCell>{t_('Code')}</TableCell>
                <TableCell>{t_('Batch')}</TableCell>
                <TableCell align='right'>{t_('Available in general stock')}</TableCell>
                {partial && <TableCell align='right'>{t_('Quantity to load')}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {instances.map((row) => (
                <TableRow key={row.id} hover>
                  {!partial && (
                    <TableCell padding='checkbox'>
                      <Checkbox
                        size='small'
                        checked={selectedIds.includes(row.id)}
                        onChange={() => toggleInstance(row.id)}
                        inputProps={{ 'aria-label': t_('Select instance') }}
                      />
                    </TableCell>
                  )}
                  <TableCell>{row.code}</TableCell>
                  <TableCell>{row.batch}</TableCell>
                  <TableCell align='right'>{row.quantityAvailable}</TableCell>
                  {partial && (
                    <TableCell align='right'>
                      <TextField
                        type='number'
                        size='small'
                        value={quantityByInstanceId[row.id] ?? ''}
                        onChange={(event) => setQuantity(row.id, event.target.value)}
                        inputProps={{ min: 0, max: row.quantityAvailable, step: allowDecimal ? 'any' : 1 }}
                      />
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {selectedCount > 0 && (
            <Typography variant='body2' color='text.secondary'>{selectedCount + ' ' + t_('items selected')}</Typography>
          )}
        </Stack>
      }
    />
  )
}

export default LoadMachineStorageInstancesDialog
